import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

// @mui material components
import {
  Alert,
  Box,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import { PageListContainer } from "../../../components/Page";
import {
  QuotaStatusResponse,
  UserSubscription,
} from "../../../types/subscription";
import { quotaService } from "../../../services/quotaService";
import { userSubscriptionService } from "../../../services/userSubscriptionService";

import { formatDateTime } from "../../../lib/datetime/displayDateTime";

const formatDateShort = (value?: string) => formatDateTime(value, "date") || "—";
const formatQuotaDateTime = (value?: string) => formatDateTime(value) || "—";

type PeriodQuota = QuotaStatusResponse["chatMessageDaily"];

const CompactQuotaRow = ({ label, value }: { label: string; value?: PeriodQuota }) => {
  const { t } = useTranslation();
  const remaining = value?.remaining ?? 0;
  const limit = value?.limit ?? 0;
  const isUnlimited = limit === -1 || remaining === -1;
  const usedPct =
    limit > 0 ? Math.min(100, Math.round(((limit - remaining) / limit) * 100)) : 0;
  const barColor = usedPct >= 90 ? "error" : usedPct >= 70 ? "warning" : "success";

  return (
    <Box
      sx={{
        py: 1,
        px: 1.25,
        borderRadius: 1,
        bgcolor: "action.hover",
        border: 1,
        borderColor: "divider",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.04}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.primary" fontWeight={600} sx={{ fontVariantNumeric: "tabular-nums" }}>
          {isUnlimited ? t("common.unlimited") : `${remaining} / ${limit}`}
        </Typography>
      </Stack>
      {!isUnlimited && (
        <LinearProgress
          variant="determinate"
          value={usedPct}
          color={barColor}
          sx={{ mt: 0.75, height: 4, borderRadius: 1 }}
        />
      )}
    </Box>
  );
};

const MetaRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2} sx={{ py: 0.35 }}>
    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      color="text.primary"
      fontWeight={500}
      sx={{ textAlign: "right", minWidth: 0, wordBreak: "break-word" }}
    >
      {value}
    </Typography>
  </Stack>
);

function SubscriptionPage() {
  const { t } = useTranslation();
  const [mySubscription, setMySubscription] = useState<UserSubscription | null>(null);
  const [myQuota, setMyQuota] = useState<QuotaStatusResponse | null>(null);

  const loadBase = useCallback(async () => {
    try {
      const [mySubRes, myQuotaRes] = await Promise.all([
        userSubscriptionService.mySubscription().catch(() => null),
        quotaService.myQuota().catch(() => null),
      ]);
      setMySubscription(mySubRes);
      setMyQuota(myQuotaRes);
    } catch {
      // no-op
    }
  }, []);

  useEffect(() => {
    loadBase();
  }, [loadBase]);

  const daysLeft =
    mySubscription?.endDate != null
      ? Math.ceil((new Date(mySubscription.endDate).getTime() - Date.now()) / 86400000)
      : null;

  const planLabel = myQuota?.planName || mySubscription?.planSnapshot?.name || "—";
  const status = mySubscription?.status;
  const statusColor =
    status === "CANCELLED" ? "default" : status === "PASSIVE" ? "warning" : "success";

  const hasMailQuota =
    myQuota?.mailAnalysisDaily != null ||
    myQuota?.mailAnalysisWeekly != null ||
    myQuota?.mailAnalysisMonthly != null;

  const showSubscriptionCard = mySubscription != null || myQuota != null;

  return (
    <PageListContainer>
      <Stack spacing={2}>
        {mySubscription == null ? (
          <Alert severity="warning">No active subscription found for your account.</Alert>
        ) : daysLeft != null && daysLeft <= 7 ? (
          <Alert severity="warning">
            Your subscription expires in {daysLeft} day{daysLeft === 1 ? "" : "s"}.
          </Alert>
        ) : null}

        {showSubscriptionCard && (
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 1.5, sm: 2 },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
              spacing={1.5}
              sx={{ mb: 1.5 }}
            >
              <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                My Subscription
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                {mySubscription != null && daysLeft != null && daysLeft > 0 && (
                  <Chip
                    size="small"
                    label={daysLeft <= 7 ? `${daysLeft}d left` : `${daysLeft} days left`}
                    color={daysLeft <= 7 ? "warning" : "default"}
                    variant={daysLeft <= 7 ? "filled" : "outlined"}
                    sx={{ fontWeight: 600 }}
                  />
                )}
                {mySubscription != null && (
                  <Chip
                    size="small"
                    label={status || "ACTIVE"}
                    color={statusColor}
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                )}
              </Stack>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 5 }}>
                <Box
                  sx={{
                    height: "100%",
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: "background.default",
                    border: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 0.08 }}>
                    {t("subscription.planAndTerm")}
                  </Typography>
                  <Typography variant="body1" fontWeight={700} sx={{ mt: 0.5, mb: 1.5, lineHeight: 1.3 }}>
                    {planLabel}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <MetaRow label={t("subscription.start")} value={formatDateShort(mySubscription?.startDate)} />
                  <MetaRow label={t("subscription.end")} value={formatDateShort(mySubscription?.endDate)} />
                  {mySubscription?.durationType && (
                    <MetaRow label={t("subscription.billing")} value={t(`options.durationType.${mySubscription.durationType}`, { defaultValue: mySubscription.durationType.replace("_", " ") })} />
                  )}
                  {mySubscription?.recurringMonths != null && (
                    <MetaRow label={t("subscription.cycle")} value={t("subscription.cycleMonths", { count: mySubscription.recurringMonths })} />
                  )}
                  {myQuota?.subscriptionEndDate && (
                    <MetaRow label={t("subscription.quotaResetRef")} value={formatQuotaDateTime(myQuota.subscriptionEndDate)} />
                  )}
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 7 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  fontWeight={700}
                  sx={{ letterSpacing: 0.08, display: "block", mb: 1 }}
                >
                  {t("subscription.chatQuota")}
                </Typography>
                <Stack spacing={1}>
                  <CompactQuotaRow label={t("subscription.daily")} value={myQuota?.chatMessageDaily} />
                  <CompactQuotaRow label={t("subscription.weekly")} value={myQuota?.chatMessageWeekly} />
                  <CompactQuotaRow label={t("subscription.monthly")} value={myQuota?.chatMessageMonthly} />
                </Stack>

                {hasMailQuota && (
                  <>
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      fontWeight={700}
                      sx={{ letterSpacing: 0.08, display: "block", mt: 2, mb: 1 }}
                    >
                      {t("subscription.mailQuota")}
                    </Typography>
                    <Stack spacing={1}>
                      <CompactQuotaRow label={t("subscription.daily")} value={myQuota?.mailAnalysisDaily} />
                      <CompactQuotaRow label={t("subscription.weekly")} value={myQuota?.mailAnalysisWeekly} />
                      <CompactQuotaRow label={t("subscription.monthly")} value={myQuota?.mailAnalysisMonthly} />
                    </Stack>
                  </>
                )}
              </Grid>
            </Grid>
          </Paper>
        )}
      </Stack>
    </PageListContainer>
  );
}

export default SubscriptionPage;
